import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes
} from 'react';
import { createPortal } from 'react-dom';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import { useAppContext } from '@/Main';
import Icon, { type IconName } from './Icon';
import type { ClusterViewMode } from '../types';
import '../styles/cluster-tree.css';

const COMPACT_QUERY = '(max-width: 991.98px)';

/** One step darker than body background in dark theme. */
const DARK_TRACK_BG =
  'color-mix(in srgb, var(--falcon-body-bg, #0b1727) 78%, #000)';

/** Render on body so the open menu paints above the grid, not inside the page stack. */
const MenuInBody = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    show?: boolean;
    close?: () => void;
    align?: unknown;
  }
>(function MenuInBody({ show: _show, close: _close, align: _align, ...props }, ref) {
  if (typeof document === 'undefined') return null;
  return createPortal(<div ref={ref} {...props} />, document.body);
});

type ClusterViewModeSwitcherProps = {
  value: string;
  onChange?: (id: string) => void;
  modes: ClusterViewMode[];
  'aria-label'?: string;
  className?: string;
};

export default function ClusterViewModeSwitcher({
  value,
  onChange,
  modes,
  'aria-label': ariaLabel = 'نوع نمایش',
  className = ''
}: ClusterViewModeSwitcherProps) {
  const {
    config: { isDark }
  } = useAppContext();
  const trackRef = useRef<HTMLDivElement>(null);
  const widthsRef = useRef<number[]>([]);
  const [compact, setCompact] = useState(false);
  const [cut, setCut] = useState(modes.length);
  const modeKey = modes.map(mode => mode.id).join('|');

  useLayoutEffect(() => {
    const root = trackRef.current;
    if (!root) return;

    const fit = () => {
      const isCompact = window.matchMedia(COMPACT_QUERY).matches;
      setCompact(prev => (prev === isCompact ? prev : isCompact));

      root.querySelectorAll<HTMLElement>('[data-mode-id]').forEach(tab => {
        const index = Number(tab.dataset.index);
        if (tab.offsetWidth > 0) widthsRef.current[index] = tab.offsetWidth;
      });

      if (!isCompact) {
        setCut(modes.length);
        return;
      }

      const widths = modes.map((_, index) => widthsRef.current[index] ?? 0);
      if (widths.some(width => width <= 0)) {
        setCut(modes.length);
        return;
      }

      const styles = getComputedStyle(root);
      const gap = parseFloat(styles.columnGap) || 0;
      const available =
        root.clientWidth -
        (parseFloat(styles.paddingLeft) || 0) -
        (parseFloat(styles.paddingRight) || 0);
      const moreW =
        root.querySelector<HTMLElement>('[data-mode-more]')?.offsetWidth ?? 46;
      const reserve = moreW + gap;

      let used = 0;
      let next = widths.length;
      for (let i = 0; i < widths.length; i++) {
        if (used + widths[i] > available - (i === widths.length - 1 ? 0 : reserve) + 1) {
          next = i;
          break;
        }
        used += widths[i] + gap;
      }

      setCut(prev => (prev === next ? prev : next));
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(root);
    const query = window.matchMedia(COMPACT_QUERY);
    query.addEventListener('change', fit);
    return () => {
      observer.disconnect();
      query.removeEventListener('change', fit);
    };
  }, [compact, cut, modeKey, modes.length]);

  const hiddenIds = new Set(
    compact ? modes.slice(cut).map(mode => mode.id) : []
  );
  if (hiddenIds.has(value)) {
    const shown = modes.filter(mode => !hiddenIds.has(mode.id));
    const displaced = shown[shown.length - 1];
    if (displaced) {
      hiddenIds.delete(value);
      hiddenIds.add(displaced.id);
    }
  }
  const overflow = modes.filter(mode => hiddenIds.has(mode.id));
  const overflowHasActive = overflow.some(mode => mode.id === value);

  return (
    <div
      ref={trackRef}
      className={`d-inline-flex flex-nowrap align-items-center gap-1 p-1 rounded-pill border${
        compact ? ' w-100' : ''
      }${isDark ? '' : ' bg-white'}${className ? ` ${className}` : ''}`}
      style={{
        maxWidth: '100%',
        ...(compact ? { width: '100%', minWidth: 0 } : {}),
        ...(isDark ? { backgroundColor: DARK_TRACK_BG } : {})
      }}
      role="tablist"
      aria-label={ariaLabel}
      dir="rtl"
    >
      {modes.map((mode, index) => {
        const active = value === mode.id;
        return (
          <Button
            key={mode.id}
            type="button"
            role="tab"
            size="sm"
            data-mode-id={mode.id}
            data-index={index}
            aria-selected={active}
            disabled={mode.disabled}
            variant={active ? 'falcon-primary' : 'link'}
            className={`rounded-pill flex-shrink-0 d-inline-flex align-items-center gap-1 px-3 py-2 shadow-none text-decoration-none${
              hiddenIds.has(mode.id) ? ' cluster-mode-tab--overflow' : ''
            }${active ? '' : ' text-600'}`}
            onClick={() => onChange?.(mode.id)}
          >
            {mode.icon ? (
              <Icon name={mode.icon as IconName} size="0.85rem" />
            ) : null}
            <span className="fs--1 fw-semi-bold d-inline-flex align-items-center gap-1">
              {mode.label}
            </span>
          </Button>
        );
      })}
      {compact && overflow.length > 0 ? (
        <div
          data-mode-more
          className="d-inline-flex flex-shrink-0 ms-auto"
          style={{ marginInlineStart: 'auto' }}
        >
          <Dropdown drop="end" align="start">
            <Dropdown.Toggle
              variant={overflowHasActive ? 'falcon-primary' : 'link'}
              size="sm"
              className={`cluster-mode-more rounded-pill px-3 py-2 shadow-none text-decoration-none${
                overflowHasActive ? '' : ' text-600'
              }`}
              aria-label="سایر نمایش‌ها"
            >
              <span
                aria-hidden
                className="lh-1"
                style={{ fontWeight: 600, fontSize: '1.05rem', letterSpacing: '0.04em' }}
              >
                •••
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu
              as={MenuInBody}
              className="cluster-mode-more-menu shadow border-0 py-1"
              dir="rtl"
              flip={false}
              popperConfig={{
                strategy: 'fixed',
                modifiers: [{ name: 'offset', options: { offset: [0, -12] } }]
              }}
            >
              {overflow.map(mode => (
                <Dropdown.Item
                  key={mode.id}
                  active={value === mode.id}
                  disabled={mode.disabled}
                  onClick={() => onChange?.(mode.id)}
                  className="d-flex align-items-center gap-2 fs--1"
                >
                  {mode.icon ? (
                    <Icon name={mode.icon as IconName} size="0.85rem" />
                  ) : null}
                  {mode.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ) : null}
    </div>
  );
}
